import { Zap, X, Check } from "lucide-react";
import { LinkButton } from "@/components/ui/LinkButton";
import { DRIVER_PLAN_DETAILS } from "@ridereserv/shared/constants";
import { analytics } from "@/lib/analytics";
import { cn } from "@ridereserv/shared/utils";

interface UpgradePlanCardProps {
  /**
   * Visual variant of the card.
   * - sidebar: Compact card for sidebar placement
   * - expanded: Larger card with more feature details
   */
  variant?: "sidebar" | "expanded";
  /**
   * Location identifier for analytics tracking.
   */
  location: string;
  /**
   * Callback when the card is dismissed.
   */
  onDismiss?: () => void;
  /**
   * Additional CSS classes.
   */
  className?: string;
}

const paidPlan = DRIVER_PLAN_DETAILS.paid_driver;

/**
 * Upgrade CTA card for free-tier drivers.
 * Displays the benefits of upgrading to Professional plan.
 * @param root0
 * @param root0.variant
 * @param root0.location
 * @param root0.onDismiss
 * @param root0.className
 * @returns The upgrade plan card component
 */
export function UpgradePlanCard({
  variant = "sidebar",
  location,
  onDismiss,
  className,
}: UpgradePlanCardProps) {
  const handleClick = () => {
    analytics.trackCtaClick(location, "upgrade", "/account/select-plan");
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onDismiss?.();
  };

  if (variant === "sidebar") {
    return (
      <div
        className={cn(
          "relative rounded-md border border-sidebar-border bg-sidebar-hover p-3",
          className
        )}
      >
        {/* Dismiss button - positioned in top-right corner */}
        {onDismiss && (
          <button
            onClick={handleDismiss}
            className="absolute right-1 top-1 z-10 flex min-h-11 min-w-11 items-center justify-center rounded-full text-sidebar-muted transition-colors hover:text-sidebar-foreground pointer-fine:min-h-8 pointer-fine:min-w-8"
            aria-label="Dismiss"
          >
            <X className="h-3.5 w-3.5" strokeWidth={1.75} />
          </button>
        )}

        <div className={cn("flex items-center gap-2", onDismiss && "pr-6")}>
          <Zap className="h-4 w-4 shrink-0 text-upgrade-500" strokeWidth={1.75} />
          <div className="min-w-0">
            <p className="text-sm font-medium text-sidebar-foreground">Go Pro</p>
            <p className="text-xs text-sidebar-muted">
              <span className="font-mono tabular-nums">${paidPlan.price}</span>/month
            </p>
          </div>
        </div>
        <p className="mt-2 text-xs text-sidebar-muted">
          Get {paidPlan.reservationsPerMonth} reservations/month, calendar sync & more.
        </p>
        <LinkButton
          to="/account/select-plan"
          onClick={handleClick}
          size="sm"
          className="mt-3 w-full bg-upgrade-600 text-white hover:bg-upgrade-500"
        >
          Upgrade Now
        </LinkButton>
      </div>
    );
  }

  // Expanded variant with more details
  return (
    <div
      className={cn(
        "relative rounded-md border border-upgrade-200 bg-upgrade-50 p-6",
        className
      )}
    >
      {/* Dismiss button - positioned in top-right corner */}
      {onDismiss && (
        <button
          onClick={handleDismiss}
          className="absolute right-2 top-2 z-10 flex min-h-11 min-w-11 items-center justify-center rounded-full text-upgrade-600 hover:bg-upgrade-100 hover:text-upgrade-700 pointer-fine:min-h-8 pointer-fine:min-w-8"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" strokeWidth={1.75} />
        </button>
      )}

      <div className={cn("flex items-center gap-3", onDismiss && "pr-8")}>
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-upgrade-200 text-upgrade-600">
          <Zap className="h-5 w-5" strokeWidth={1.75} />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-upgrade-900">
            Upgrade to {paidPlan.name}
          </h3>
          <p className="text-sm text-upgrade-700">
            <span className="font-mono tabular-nums">${paidPlan.price}</span>/month - Unlock your full potential
          </p>
        </div>
      </div>
      <ul className="mt-4 space-y-2">
        {paidPlan.features.slice(0, 4).map((feature, idx) => (
          <li key={idx} className="flex items-center gap-2 text-sm text-neutral-700">
            <Check className="h-4 w-4 text-upgrade-600" strokeWidth={1.75} />
            {feature}
          </li>
        ))}
      </ul>
      <LinkButton
        to="/account/select-plan"
        onClick={handleClick}
        className="mt-4 w-full bg-upgrade-600 hover:bg-upgrade-700"
      >
        Upgrade to Professional
      </LinkButton>
    </div>
  );
}
